/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Support Tools Page - SuperAdmin Portal
 * ============================================================================
 *
 * User lookup and account management tools for support operations.
 * Includes Reset Password, Unlock Account, and Disable MFA functionality.
 *
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LifeBuoy, Search, User, Building2, Shield, Clock,
  Mail, Key, Unlock, ShieldOff, CheckCircle, XCircle,
  AlertTriangle, FileText, Wallet, Settings, ClipboardList,
  UserX, UserCheck, Ban, Play
} from 'lucide-react';
import { supportService } from '@/services/superadmin/support.service';
import { get } from '@/services/api/client';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';

// Types
interface UserLookupResult {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgName: string;
  orgId: string;
  role: string;
  status: 'ACTIVE' | 'LOCKED' | 'SUSPENDED' | 'DEACTIVATED' | 'PENDING_ACTIVATION' | 'PENDING_VERIFICATION';
  mfaEnabled: boolean;
  mfaEnforced: boolean;
  failedLoginAttempts: number;
  lastLogin: string | null;
  createdAt: string;
}

// Status Badge
const StatusBadge: React.FC<{ status: UserLookupResult['status']; t: (key: string) => string }> = ({ status, t }) => {
  const config: Record<UserLookupResult['status'], { bg: string; text: string; icon: React.ElementType }> = {
    ACTIVE: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle },
    LOCKED: { bg: 'bg-red-500/15', text: 'text-red-500', icon: XCircle },
    SUSPENDED: { bg: 'bg-orange-500/15', text: 'text-orange-500', icon: Ban },
    DEACTIVATED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: UserX },
    PENDING_ACTIVATION: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: Clock },
    PENDING_VERIFICATION: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: Clock },
  };
  const { bg, text, icon: Icon } = config[status];

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(`supportTools.status.${status.toLowerCase()}`)}
    </span>
  );
};

// Confirmation Modal
const ConfirmModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmColor: 'red' | 'green' | 'amber';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  t: (key: string) => string;
}> = ({ isOpen, title, message, confirmText, confirmColor, onConfirm, onCancel, loading, t }) => {
  if (!isOpen) return null;

  const colorStyles = {
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-emerald-600 hover:bg-emerald-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="modal-content">
        <div className="modal-icon">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
        </div>
        <h3 className="modal-title">{title}</h3>
        <p className="modal-message">{message}</p>
        <div className="modal-actions">
          <button
            onClick={onCancel}
            disabled={loading}
            className="btn btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`btn ${colorStyles[confirmColor]} text-white`}
          >
            {loading ? t('supportTools.modal.processing') : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick Action Card
const QuickActionCard: React.FC<{
  href: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: number;
}> = ({ href, icon, title, description, badge }) => (
  <Link to={href} className="quick-action-card">
    <div className="quick-action-icon">{icon}</div>
    <div className="quick-action-content">
      <div className="quick-action-title">
        {title}
        {badge !== undefined && badge > 0 && (
          <span className="quick-action-badge">{badge}</span>
        )}
      </div>
      <div className="quick-action-desc">{description}</div>
    </div>
  </Link>
);

// Main Support Tools Page
export default function SupportToolsPage() {
  const { t } = useTranslation('platform');
  const [searchEmail, setSearchEmail] = useState('');
  const [userResult, setUserResult] = useState<UserLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Modal states
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [unlockAccountModal, setUnlockAccountModal] = useState(false);
  const [disableMfaModal, setDisableMfaModal] = useState(false);
  const [enforceMfaModal, setEnforceMfaModal] = useState(false);
  const [removeMfaEnforcementModal, setRemoveMfaEnforcementModal] = useState(false);
  const [suspendAccountModal, setSuspendAccountModal] = useState(false);
  const [activateAccountModal, setActivateAccountModal] = useState(false);
  const [deactivateAccountModal, setDeactivateAccountModal] = useState(false);

  // Pending counts for quick actions
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  // Fetch pending counts on mount
  useEffect(() => {
    const fetchPendingCounts = async () => {
      try {
        // Fetch pending registrations count
        const regData = await get<{ data?: { page?: { totalElements?: number } } }>('/superadmin/registrations', { status: 'PENDING', page: 0, size: 1 });
        setPendingRegistrations(regData.data?.page?.totalElements || 0);
      } catch (error) {
        console.error('Failed to fetch pending registrations:', error);
      }

      try {
        // Fetch pending withdrawals count
        const wdData = await get<{ data?: { page?: { totalElements?: number } } }>('/superadmin/withdrawals', { status: 'PENDING', page: 0, size: 1 });
        setPendingWithdrawals(wdData.data?.page?.totalElements || 0);
      } catch (error) {
        console.error('Failed to fetch pending withdrawals:', error);
      }
    };

    fetchPendingCounts();
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleUserLookup = async () => {
    if (!searchEmail.trim()) return;

    setLoading(true);
    setNotification(null);
    setUserResult(null);

    try {
      const result = await supportService.lookupUser(searchEmail);
      setUserResult(result as unknown as UserLookupResult);
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string };
      if (err.status === 400 || err.message?.includes('not found')) {
        showNotification('error', t('supportTools.notifications.userNotFound', { email: searchEmail }));
      } else {
        showNotification('error', t('supportTools.notifications.lookupError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.sendPasswordReset(userResult.id);
      showNotification('success', t('supportTools.notifications.passwordResetSent', { email: userResult.email }));
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.resetPasswordError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setResetPasswordModal(false);
    }
  };

  const handleUnlockAccount = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.unlockAccount(userResult.id);
      showNotification('success', t('supportTools.notifications.accountUnlocked', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, status: 'ACTIVE', failedLoginAttempts: 0 } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.unlockError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setUnlockAccountModal(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.disableMfa(userResult.id);
      showNotification('success', t('supportTools.notifications.mfaDisabled', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, mfaEnabled: false } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.disableMfaError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setDisableMfaModal(false);
    }
  };

  const handleEnforceMFA = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.enforceMfa(userResult.id);
      showNotification('success', t('supportTools.notifications.mfaEnforced', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, mfaEnforced: true } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.enforceMfaError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setEnforceMfaModal(false);
    }
  };

  const handleRemoveMfaEnforcement = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.removeMfaEnforcement(userResult.id);
      showNotification('success', t('supportTools.notifications.mfaEnforcementRemoved', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, mfaEnforced: false } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.removeMfaEnforcementError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setRemoveMfaEnforcementModal(false);
    }
  };

  const handleSuspendAccount = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.suspendAccount(userResult.id);
      showNotification('success', t('supportTools.notifications.accountSuspended', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, status: 'SUSPENDED' } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.suspendAccountError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setSuspendAccountModal(false);
    }
  };

  const handleActivateAccount = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.activateAccount(userResult.id);
      showNotification('success', t('supportTools.notifications.accountActivated', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, status: 'ACTIVE' } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.activateAccountError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setActivateAccountModal(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!userResult) return;

    setActionLoading(true);
    try {
      await supportService.deactivateAccount(userResult.id);
      showNotification('success', t('supportTools.notifications.accountDeactivated', { email: userResult.email }));
      setUserResult(prev => prev ? { ...prev, status: 'DEACTIVATED' } : null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('supportTools.notifications.deactivateAccountError') };
      showNotification('error', message);
    } finally {
      setActionLoading(false);
      setDeactivateAccountModal(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('supportTools.never');
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="support-tools-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <LifeBuoy className="w-6 h-6" /> {t('supportTools.title')}
          </h1>
          <p className="page-subtitle">{t('supportTools.subtitle')}</p>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* User Lookup Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            <Search className="w-5 h-5" /> {t('supportTools.userLookup.title')}
          </h2>
        </div>
        <div className="card-body">
          {/* Search Input */}
          <div className="search-row">
            <div className="search-input-wrapper">
              <Mail className="search-icon" />
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder={t('supportTools.userLookup.placeholder')}
                className="search-input"
                onKeyDown={(e) => e.key === 'Enter' && handleUserLookup()}
              />
            </div>
            <button
              onClick={handleUserLookup}
              disabled={loading || !searchEmail.trim()}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  {t('supportTools.userLookup.searching')}
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  {t('supportTools.userLookup.search')}
                </>
              )}
            </button>
          </div>

          {/* Search Hints */}
          <div className="search-hints">
            <span className="hint-label">{t('supportTools.userLookup.hint')}</span>
          </div>

          {/* User Result */}
          {userResult && (
            <div className="user-result">
              {/* User Info Grid */}
              <div className="user-info-grid">
                <div className="user-info-item">
                  <User className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.name')}</div>
                    <div className="info-value">{userResult.firstName} {userResult.lastName}</div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Mail className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.email')}</div>
                    <div className="info-value">{userResult.email}</div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Building2 className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.organization')}</div>
                    <div className="info-value">{userResult.orgName}</div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Shield className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.role')}</div>
                    <div className="info-value">{userResult.role}</div>
                  </div>
                </div>
                <div className="user-info-item">
                  <div className="info-icon-placeholder"></div>
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.status')}</div>
                    <div className="info-value">
                      <StatusBadge status={userResult.status} t={t} />
                      {userResult.failedLoginAttempts > 0 && (
                        <span className="failed-attempts">
                          ({t('supportTools.userInfo.failedAttempts', { count: userResult.failedLoginAttempts })})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Key className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.mfaStatus')}</div>
                    <div className="info-value">
                      {userResult.mfaEnabled ? (
                        <span className="mfa-enabled">{t('supportTools.mfa.enabled')}</span>
                      ) : userResult.mfaEnforced ? (
                        <span className="mfa-enforced">{t('supportTools.mfa.enforced')}</span>
                      ) : (
                        <span className="mfa-disabled">{t('supportTools.mfa.disabled')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Clock className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.lastLogin')}</div>
                    <div className="info-value">{formatDate(userResult.lastLogin)}</div>
                  </div>
                </div>
                <div className="user-info-item">
                  <Clock className="info-icon" />
                  <div>
                    <div className="info-label">{t('supportTools.userInfo.accountCreated')}</div>
                    <div className="info-value">{formatDate(userResult.createdAt)}</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="user-actions">
                <button
                  onClick={() => setResetPasswordModal(true)}
                  className="action-btn action-btn-warning"
                >
                  <Key className="w-4 h-4" />
                  {t('supportTools.actions.resetPassword')}
                </button>

                {userResult.status === 'LOCKED' && (
                  <button
                    onClick={() => setUnlockAccountModal(true)}
                    className="action-btn action-btn-success"
                  >
                    <Unlock className="w-4 h-4" />
                    {t('supportTools.actions.unlockAccount')}
                  </button>
                )}

                {userResult.mfaEnabled ? (
                  <button
                    onClick={() => setDisableMfaModal(true)}
                    className="action-btn action-btn-danger"
                  >
                    <ShieldOff className="w-4 h-4" />
                    {t('supportTools.actions.disableMfa')}
                  </button>
                ) : userResult.mfaEnforced ? (
                  <button
                    onClick={() => setRemoveMfaEnforcementModal(true)}
                    className="action-btn action-btn-warning"
                  >
                    <ShieldOff className="w-4 h-4" />
                    {t('supportTools.actions.removeMfaEnforcement')}
                  </button>
                ) : (
                  <button
                    onClick={() => setEnforceMfaModal(true)}
                    className="action-btn action-btn-info"
                  >
                    <Shield className="w-4 h-4" />
                    {t('supportTools.actions.enforceMfa')}
                  </button>
                )}

                {/* Account Status Actions */}
                {userResult.status === 'ACTIVE' && (
                  <button
                    onClick={() => setSuspendAccountModal(true)}
                    className="action-btn action-btn-warning"
                  >
                    <Ban className="w-4 h-4" />
                    {t('supportTools.actions.suspendAccount')}
                  </button>
                )}

                {(userResult.status === 'SUSPENDED' || userResult.status === 'LOCKED' ||
                  userResult.status === 'PENDING_ACTIVATION' || userResult.status === 'PENDING_VERIFICATION' ||
                  userResult.status === 'DEACTIVATED') && (
                  <button
                    onClick={() => setActivateAccountModal(true)}
                    className="action-btn action-btn-success"
                  >
                    <Play className="w-4 h-4" />
                    {t('supportTools.actions.activateAccount')}
                  </button>
                )}

                {userResult.status !== 'DEACTIVATED' && (
                  <button
                    onClick={() => setDeactivateAccountModal(true)}
                    className="action-btn action-btn-danger"
                  >
                    <UserX className="w-4 h-4" />
                    {t('supportTools.actions.deactivateAccount')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('supportTools.quickActions.title')}</h2>
        </div>
        <div className="quick-actions-grid">
          <QuickActionCard
            href="/superadmin/audit-logs"
            icon={<ClipboardList className="w-5 h-5" />}
            title={t('supportTools.quickActions.auditLogs')}
            description={t('supportTools.quickActions.auditLogsDesc')}
          />
          <QuickActionCard
            href="/superadmin/registrations"
            icon={<FileText className="w-5 h-5" />}
            title={t('supportTools.quickActions.pendingRegistrations')}
            description={t('supportTools.quickActions.pendingRegistrationsDesc')}
            badge={pendingRegistrations}
          />
          <QuickActionCard
            href="/superadmin/withdrawals"
            icon={<Wallet className="w-5 h-5" />}
            title={t('supportTools.quickActions.pendingWithdrawals')}
            description={t('supportTools.quickActions.pendingWithdrawalsDesc')}
            badge={pendingWithdrawals}
          />
          <QuickActionCard
            href="/superadmin/settings"
            icon={<Settings className="w-5 h-5" />}
            title={t('supportTools.quickActions.platformSettings')}
            description={t('supportTools.quickActions.platformSettingsDesc')}
          />
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={resetPasswordModal}
        title={t('supportTools.dialogs.resetPassword.title')}
        message={t('supportTools.dialogs.resetPassword.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.resetPassword.confirm')}
        confirmColor="amber"
        onConfirm={handleResetPassword}
        onCancel={() => setResetPasswordModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={unlockAccountModal}
        title={t('supportTools.dialogs.unlockAccount.title')}
        message={t('supportTools.dialogs.unlockAccount.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.unlockAccount.confirm')}
        confirmColor="green"
        onConfirm={handleUnlockAccount}
        onCancel={() => setUnlockAccountModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={disableMfaModal}
        title={t('supportTools.dialogs.disableMfa.title')}
        message={t('supportTools.dialogs.disableMfa.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.disableMfa.confirm')}
        confirmColor="red"
        onConfirm={handleDisableMFA}
        onCancel={() => setDisableMfaModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={enforceMfaModal}
        title={t('supportTools.dialogs.enforceMfa.title')}
        message={t('supportTools.dialogs.enforceMfa.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.enforceMfa.confirm')}
        confirmColor="amber"
        onConfirm={handleEnforceMFA}
        onCancel={() => setEnforceMfaModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={removeMfaEnforcementModal}
        title={t('supportTools.dialogs.removeMfaEnforcement.title')}
        message={t('supportTools.dialogs.removeMfaEnforcement.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.removeMfaEnforcement.confirm')}
        confirmColor="amber"
        onConfirm={handleRemoveMfaEnforcement}
        onCancel={() => setRemoveMfaEnforcementModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={suspendAccountModal}
        title={t('supportTools.dialogs.suspendAccount.title')}
        message={t('supportTools.dialogs.suspendAccount.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.suspendAccount.confirm')}
        confirmColor="amber"
        onConfirm={handleSuspendAccount}
        onCancel={() => setSuspendAccountModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={activateAccountModal}
        title={t('supportTools.dialogs.activateAccount.title')}
        message={t('supportTools.dialogs.activateAccount.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.activateAccount.confirm')}
        confirmColor="green"
        onConfirm={handleActivateAccount}
        onCancel={() => setActivateAccountModal(false)}
        loading={actionLoading}
        t={t}
      />

      <ConfirmModal
        isOpen={deactivateAccountModal}
        title={t('supportTools.dialogs.deactivateAccount.title')}
        message={t('supportTools.dialogs.deactivateAccount.message', { email: userResult?.email })}
        confirmText={t('supportTools.dialogs.deactivateAccount.confirm')}
        confirmColor="red"
        onConfirm={handleDeactivateAccount}
        onCancel={() => setDeactivateAccountModal(false)}
        loading={actionLoading}
        t={t}
      />

      <style>{supportToolsStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const supportToolsStyles = `
  .support-tools-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  /* Notification */
  .notification {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
  }

  .notification.success {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  .notification.error {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }

  /* Card */
  .card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
  }

  .card-header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .card-body {
    padding: 20px;
  }

  /* Search */
  .search-row {
    display: flex;
    gap: 12px;
  }

  .search-input-wrapper {
    flex: 1;
    position: relative;
  }

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: var(--color-text-muted);
  }

  .search-input {
    width: 100%;
    height: 44px;
    padding: 0 16px 0 44px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text-primary);
    outline: none;
    transition: border-color 0.15s ease;
  }

  .search-input:focus {
    border-color: var(--color-brand-primary);
  }

  .search-input::placeholder {
    color: var(--color-text-muted);
  }

  /* Search Hints */
  .search-hints {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 12px;
  }

  .hint-label {
    color: var(--color-text-muted);
  }

  .hint-email {
    padding: 4px 8px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 4px;
    color: var(--color-text-secondary);
    font-size: 11px;
    font-family: monospace;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .hint-email:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    height: 44px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  /* Spinner */
  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* User Result */
  .user-result {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .user-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .user-info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .info-icon {
    width: 18px;
    height: 18px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  .info-icon-placeholder {
    width: 18px;
  }

  .info-label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 14px;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .failed-attempts {
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .mfa-enabled {
    color: #10B981;
    font-weight: 500;
  }

  .mfa-disabled {
    color: var(--color-text-muted);
  }

  .mfa-enforced {
    color: #F59E0B;
    font-weight: 500;
  }

  /* User Actions */
  .user-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .action-btn-warning {
    background: rgba(245, 158, 11, 0.15);
    color: #F59E0B;
  }

  .action-btn-warning:hover {
    background: rgba(245, 158, 11, 0.25);
  }

  .action-btn-success {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .action-btn-success:hover {
    background: rgba(16, 185, 129, 0.25);
  }

  .action-btn-danger {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
  }

  .action-btn-danger:hover {
    background: rgba(239, 68, 68, 0.25);
  }

  .action-btn-info {
    background: rgba(59, 130, 246, 0.15);
    color: #3B82F6;
  }

  .action-btn-info:hover {
    background: rgba(59, 130, 246, 0.25);
  }

  /* Quick Actions */
  .quick-actions-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 16px;
  }

  .quick-action-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.15s ease;
  }

  .quick-action-card:hover {
    background: var(--color-bg-hover);
    border-color: var(--color-border-default);
  }

  .quick-action-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-secondary);
    border-radius: 8px;
    color: var(--color-text-secondary);
  }

  .quick-action-content {
    flex: 1;
  }

  .quick-action-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .quick-action-badge {
    padding: 2px 6px;
    background: var(--color-error);
    color: white;
    font-size: 10px;
    font-weight: 600;
    border-radius: 99px;
  }

  .quick-action-desc {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  /* Modal */
  .modal-content {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 24px;
    width: 100%;
    max-width: 420px;
    text-align: center;
  }

  .modal-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(245, 158, 11, 0.15);
    border-radius: 12px;
  }

  .modal-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 8px;
  }

  .modal-message {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.5;
    margin-bottom: 24px;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
  }

  .modal-actions .btn {
    flex: 1;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .search-row {
      flex-direction: column;
    }

    .user-info-grid {
      grid-template-columns: 1fr;
    }

    .quick-actions-grid {
      grid-template-columns: 1fr;
    }

    .user-actions {
      flex-direction: column;
    }

    .action-btn {
      justify-content: center;
    }
  }
`;
